import * as React from 'react';
import { NumberField as BaseNumberField } from '@base-ui/react/number-field';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

/**
 * This component is a placeholder for FormControl to correctly set the shrink label state on SSR.
 */
function SSRInitialFilled(_: BaseNumberField.Root.Props) {
  return null;
}
SSRInitialFilled.muiName = 'Input';

export default function NumberField({
  id: idProp,
  label,
  error,
  errorContent,
  placeholder,
  size = 'medium',
  integerOnly = false,
  ...other
}: BaseNumberField.Root.Props & {
  label?: React.ReactNode;
  size?: 'small' | 'medium';
  error?: boolean;
  errorContent?: React.ReactNode;
  placeholder?: string;
  integerOnly?: boolean;
}) {
  let id = React.useId();
  if (idProp) {
    id = idProp;
  }
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (integerOnly) {
      if (['+', '-', '.', 'e', 'E'].includes(e.key)) {
        e.preventDefault();
      }
    }
  };
  
  return (
    <BaseNumberField.Root
      {...other}
      step={integerOnly ? 1 : other.step}
      render={(props, state) => {
        const currentValue = parseFloat(state.inputValue);
        const isAboveMax = other.max !== undefined && !isNaN(currentValue) && currentValue > other.max;
        
        return (
          <FormControl
            size={size}
            ref={props.ref}
            disabled={state.disabled}
            required={state.required}
            error={error || isAboveMax}
            variant="outlined"
          >
            {props.children}
            <FormHelperText sx={{ ml: 0, '&:empty': { mt: 0 } }}>
              {isAboveMax
                ? `Deve ser menor ou igual a ${other.max}`
                : errorContent || ''}
            </FormHelperText>
          </FormControl>
        );
      }}
    >
      <SSRInitialFilled {...other} />
      <InputLabel htmlFor={id} shrink>{label}</InputLabel>
      <BaseNumberField.Input
        id={id}
        render={(props, state) => {
          const { ref, onKeyDown, ...inputProps } = props;
          return(
          <OutlinedInput
            label={label}
            inputRef={ref}
            value={state.inputValue}
            onBlur={inputProps.onBlur}
            onChange={inputProps.onChange}
            onKeyUp={inputProps.onKeyUp}
            onKeyDown={(e) => {
              handleKeyDown(e);
              onKeyDown?.(e);
            }}
            onFocus={inputProps.onFocus}
            inputProps={{
              ...inputProps,
              placeholder: placeholder,
            }}
            endAdornment={
              <InputAdornment
                position="end"
                sx={{
                  flexDirection: 'column',
                  maxHeight: 'unset',
                  alignSelf: 'stretch',
                  borderLeft: '1px solid',
                  borderColor: 'divider',
                  ml: 0,
                  '& button': {
                    py: 0,
                    flex: 1,
                    borderRadius: 0.5,
                  },
                }}
              >
                <BaseNumberField.Increment
                  render={<IconButton size={size} aria-label="Increase" />}
                >
                  <KeyboardArrowUpIcon
                    fontSize={size}
                    sx={{ transform: 'translateY(2px)' }}
                  />
                </BaseNumberField.Increment>

                <BaseNumberField.Decrement
                  render={<IconButton size={size} aria-label="Decrease" />}
                >
                  <KeyboardArrowDownIcon
                    fontSize={size}
                    sx={{ transform: 'translateY(-2px)' }}
                  />
                </BaseNumberField.Decrement>
              </InputAdornment>
            }
            sx={{ pr: 0 }}
          />
        )}}
      />
    </BaseNumberField.Root>
  )
}
